import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { dehydrate } from 'react-query';

import { CategoryDetail, ProductDetailsResponse } from '@api/catalog';
import { getBrands } from '@api/catalog/brand/api';
import { getCategoryDetail } from '@api/catalog/categories/api';
import { getProductFilters, getProducts } from '@api/catalog/product/api';
import { getPagination } from '@api/common/helpers';
import { CommonResponse } from '@api/common/types';
import { getSeoTags } from '@api/seo/api';

import { ITEMS_PER_PRODUCTS_PAGE } from '@scripts/constants';
import { fromRoubleToKopecks, getSeoFilter, isNotEmptyObject } from '@scripts/helpers';
import { getCommonProps } from '@scripts/server/getCommonProps';
import { ExtendedSSRDataProps } from '@scripts/server/types';

import { SeoData } from '@api/seo/types';
import HighLevelCategory from './components/HighLevelCategory';
import SubCategory from './components/SubCategory';

const CAROUSEL_ITEMS = 24;
const searchParams = {
    sort: 'id',
    pagination: getPagination(CAROUSEL_ITEMS),
    include: ['brand', 'category', 'images', 'attributes', 'seller'],
};

const CategoryDetailPage = ({
    isHighLevel,
    categoryId,
    total,
    dataProducts,
    seoData,
}: {
    isHighLevel?: boolean;
    categoryId?: number;
    total?: number;
    dataProducts?: ProductDetailsResponse[];
    seoData?: SeoData;
}) => {
    if (isHighLevel) return <HighLevelCategory />;

    return <SubCategory categoryId={categoryId} totalSSR={total} dataProducts={dataProducts} seoData={seoData} />;
};

export default CategoryDetailPage;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExtendedSSRDataProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        const { params } = context;

        const urlFilters = { ...context.query };
        delete urlFilters.category;
        delete urlFilters.categories;
        delete urlFilters.page;

        const parsedFilters = urlFilters?.filters ? JSON.parse(String(urlFilters?.filters)) : {};

        const price_gte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[0]) : null;
        const price_lte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[1]) : null;

        delete parsedFilters?.price;

        const preparedFilters = {
            ...parsedFilters,
            ...(price_gte && { price_gte }),
            ...(price_lte && { price_lte }),
        };

        const { key: categoryDetailKey, fetch: categoryDetailFetch } = getCategoryDetail(apiClient)({
            code: String(params?.category),
            ...(isNotEmptyObject(preparedFilters) && {
                offers_filter: preparedFilters,
            }),
        });

        try {
            const { data }: CommonResponse<CategoryDetail> = await queryClient.fetchQuery(
                categoryDetailKey,
                categoryDetailFetch
            );

            const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)(
                getSeoFilter(
                    `/catalog/${params && String(params.category)}${
                        context.query.page ? `?page=${context.query.page}` : ''
                    }`,
                    data.id
                )
            );

            const seoData = await queryClient.fetchQuery({
                queryKey: seoTagsKey,
                queryFn: baseSeoTagsFetch,
            });

            if (data?.parent_id === null) {
                if (props.isSSR) {
                    const { key: productsKey, fetch: productsFetch } = getProducts(apiClient)({
                        ...searchParams,
                        filter: { category_id: data.children.map(c => c.id) },
                        pagination: getPagination(CAROUSEL_ITEMS * data.children.length),
                    });
                    const { key: brandsKey, fetch: brandsFetch } = getBrands(apiClient)({
                        filter: { is_popular: true },
                    });

                    await Promise.all([
                        queryClient.prefetchQuery(productsKey, productsFetch),
                        queryClient.prefetchQuery(brandsKey, brandsFetch),
                    ]);
                }

                return {
                    props: {
                        dehydratedState: dehydrate(queryClient),
                        isHighLevel: true,
                        seoData,
                        ...props,
                    },
                };
            }
            const { key: filtersKey, fetch: filtersFetch } = getProductFilters(apiClient)({
                filter: {
                    category_id: Number(data.id),
                    ...preparedFilters,
                },
            });

            const { key: baseProductsCategoryKey, fetch: baseProductsCategoryFetch } = getProducts(apiClient)({
                sort: 'id',
                ...(data.id !== null && {
                    filter: {
                        category_id: data.id,
                        ...preparedFilters,
                    },
                }),
                pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                include: ['brand', 'category', 'images', 'attributes', 'seller'],
            });

            const dataProducts = await queryClient.fetchQuery({
                queryKey: baseProductsCategoryKey,
                queryFn: baseProductsCategoryFetch,
            });

            await Promise.all([
                queryClient.prefetchQuery(baseProductsCategoryKey, baseProductsCategoryFetch),
                queryClient.prefetchQuery(filtersKey, filtersFetch),
            ]);

            if (data.children.length) {
                return {
                    props: {
                        dehydratedState: dehydrate(queryClient),
                        isHighLevel: false,
                        categoryId: data.id,
                        isLeafCategory: false,
                        seoData,
                        dataProducts,
                        ...props,
                    },
                };
            }

            return {
                props: {
                    dehydratedState: dehydrate(queryClient),
                    isHighLevel: false,
                    categoryId: data.id,
                    dataProducts,
                    total: dataProducts?.meta?.pagination?.total || 0,
                    seoData,
                    isLeafCategory: true,
                    ...props,
                },
            };
        } catch (error) {
            context.res.statusCode = 404;

            return { props: { ...props, isNotFound: true } };
        }
    });
